import { LOCALE_COOKIE_NAME, LOCALE_STORAGE_KEY, SUPPORTED_LOCALES, type Locale } from './types'

const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365

/**
 * The supported interface language for a code, by base language:
 * 'En' → 'en', 'ar-AE' → 'ar', 'hi_IN' → 'hi'. null when there is no
 * catalogue for it ('es', 'zh-TW', garbage).
 */
export function toSupportedLocale(value: unknown): Locale | null {
  if (typeof value !== 'string') return null
  const base = value.trim().toLowerCase().split(/[-_]/)[0]
  return (SUPPORTED_LOCALES as readonly string[]).includes(base) ? (base as Locale) : null
}

/** The `hf-locale` cookie: every path, one year, SameSite=Lax; Secure on https. */
export function localeCookie(locale: Locale, secure: boolean): string {
  return `${LOCALE_COOKIE_NAME}=${locale}; Path=/; Max-Age=${ONE_YEAR_SECONDS}; SameSite=Lax${secure ? '; Secure' : ''}`
}

// localStorage and document.cookie can both throw (blocked site data, some
// private modes, sandboxed frames). A language preference is a convenience:
// losing it must never take the page down.
function readStorage(): string | null {
  try {
    return window.localStorage.getItem(LOCALE_STORAGE_KEY)
  } catch {
    return null
  }
}

function readCookie(): string | null {
  try {
    const prefix = `${LOCALE_COOKIE_NAME}=`
    const entry = document.cookie
      .split(';')
      .map((part) => part.trim())
      .find((part) => part.startsWith(prefix))
    return entry ? decodeURIComponent(entry.slice(prefix.length)) : null
  } catch {
    return null
  }
}

/** The saved interface language — localStorage first, then the cookie — or null when none is saved. */
export function readStoredLocale(): Locale | null {
  if (typeof window === 'undefined') return null
  return toSupportedLocale(readStorage()) ?? toSupportedLocale(readCookie())
}

/**
 * The course player's own interface language, in this tab's sessionStorage
 * (ScopedI18nProvider). The player shares the app's origin, so a choice saved
 * to `hf-locale` there became the admin app's language too (owner, 2026-10-11).
 */
export const PLAYER_LOCALE_SESSION_KEY = 'hf-locale:player'

/** A scoped screen's language for this tab, or null when none was chosen. */
export function readSessionLocale(key: string): Locale | null {
  if (typeof window === 'undefined') return null
  try {
    return toSupportedLocale(window.sessionStorage.getItem(key))
  } catch {
    return null
  }
}

/** Keep a scoped screen's choice for this tab only; never the app's `hf-locale`. */
export function persistSessionLocale(key: string, locale: Locale): void {
  if (typeof window === 'undefined' || !(SUPPORTED_LOCALES as readonly string[]).includes(locale)) return
  try {
    if (window.sessionStorage.getItem(key) !== locale) window.sessionStorage.setItem(key, locale)
  } catch {
    // Storage blocked: the choice lasts until the page closes.
  }
}

/**
 * Save the choice in localStorage and mirror it to the `hf-locale` cookie.
 * Only a catalogued language is ever written.
 */
export function persistLocale(locale: Locale): void {
  if (typeof window === 'undefined' || !(SUPPORTED_LOCALES as readonly string[]).includes(locale)) return
  try {
    if (window.localStorage.getItem(LOCALE_STORAGE_KEY) !== locale) {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, locale)
    }
  } catch {
    // Storage blocked: the cookie below still carries the choice.
  }
  try {
    if (readCookie() !== locale) {
      document.cookie = localeCookie(locale, window.location.protocol === 'https:')
    }
  } catch {
    // Cookies blocked: nothing more to do.
  }
}
